import {
  Component,
  OnInit,
  Inject,
  ChangeDetectorRef,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { BrowserUtils } from '@azure/msal-browser';
import { SpinnerService } from '../app/shared/services/spinner.service';
import { filter } from 'rxjs';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  InteractionType,
  PopupRequest,
  RedirectRequest,
} from '@azure/msal-browser';
import {
  MsalService,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import { Claim } from './shared/models/dynamic.model';
import { popupWidth, STORAGE_KEYS } from './shared/utility/constants';
import { LocalStorageService } from './shared/services/local-storage.service';
import { LocalStorageKeyService } from './shared/services/local-storage-key.service';
import { isPrintPreviewPath, normalizeAppPath } from './shared/utility/print-layout.util';
import { IBCAuthService } from './features/service/auth.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandlerService } from './core/services/error-handler.service';
import { b2cPolicies } from './auth-config';
import { MessagePopupComponent } from './shared/components/message-popup/message-popup.component';
import { ModalService } from './shared/services/modal.service';
import { CommonService } from './shared/services/common.service';
import { UserService } from './features/service/user.service';
import { UserEffectivePermissionsService } from './features/service/user-effective-permissions.service';
import { SmRpeService } from './features/service/sm-rpe.service';
import { RpeRuntimeBridgeService } from './shared/services/rpe-runtime-bridge.service';
import {
  applyRpeFieldPermissionsToForm,
  applyRpeFieldPermissionsToGridForm,
  isRpeRegistryFieldPermitted,
} from './features/service/rpe-field-permission.helpers';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})

// eslint-disable-next-line @angular-eslint/component-class-suffix
export class AppComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly localStorageKeyService = inject(LocalStorageKeyService);
  displayedColumns: string[] = ['claim', 'value'];
  dataSource: Claim[] = [];
  title = 'pactrakTNG';
  isIframe = false;
  loginDisplay = false;
  public userInfo: any;
  public isSpinnerLoad = true;
  public fetchedList: any;
  isLogout = false;
  private ipAddress!: string;
  private readonly onPopState = (): void => {
    this.commonService.clearLastUsedPayload();
  };

  private readonly userService = inject(UserService);
  private readonly userEffectivePermissionsService = inject(
    UserEffectivePermissionsService
  );
  private readonly rpeRuntimeBridge = inject(RpeRuntimeBridgeService);
  private readonly smRpeService = inject(SmRpeService);

  constructor(
    private spinnerService: SpinnerService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly ibcAuthService: IBCAuthService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly modalService: ModalService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly commonService: CommonService
  ) { }

  ngOnInit() {
    this.registerRpeRuntimeBridge();
    this.userEffectivePermissionsService.startEnforcedRouteAccessMonitor(
      this.router,
      this.destroyRef
    );
    this.spinnerStatus();
    this.b2cAuthorizeRequest();
    this.ensurePrintPopupInitialNavigation();
    this.restoreAuthRedirectUrl();
    globalThis.addEventListener('popstate', this.onPopState);
    this.schedulePlainStorageMigrationAfterNavigation();
    this.destroyRef.onDestroy(() => {
      globalThis.removeEventListener('popstate', this.onPopState);
    });
  }

  private schedulePlainStorageMigrationAfterNavigation(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        queueMicrotask(() => this.localStorageKeyService.migratePlainKeys());
      });
  }

  private ensurePrintPopupInitialNavigation(): void {
    const path = normalizeAppPath(globalThis.window.location.pathname);
    if (!isPrintPreviewPath(path)) {
      return;
    }
    const isPopup =
      BrowserUtils.isInPopup() || globalThis.window.opener != null;
    if (isPopup && !this.router.navigated) {
      void this.router.initialNavigation();
    }
  }

  private b2cAuthorizeRequest() {
    if (this.authService.instance.getActiveAccount()?.username == undefined) {
      this.spinnerService.showSpinner();
    }
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_FAILURE ||
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result: EventMessage) => {
        // Checking for the forgot password error. Learn more about B2C error codes at
        // https://learn.microsoft.com/azure/active-directory-b2c/error-codes
        if (result.error && result.error.message.indexOf('AADB2C90118') > -1) {
          let resetPasswordFlowRequest: RedirectRequest | PopupRequest = {
            authority: b2cPolicies.authorities.resetPassword.authority,
            scopes: ['openid', 'offline_access'],
          };

          this.login(resetPasswordFlowRequest);
          this.spinnerService.hideSpinner();
        }
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            (msg.eventType === EventType.LOGIN_SUCCESS ||
              msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
              msg.eventType === EventType.SSO_SILENT_SUCCESS) &&
            msg.interactionType !== InteractionType.Silent
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result: EventMessage) => {
        let payload = result.payload as AuthenticationResult;
        this.getClaims(payload.idTokenClaims);

        if (
          this.dataSource.find(x => x.claim == 'tfp')?.value ===
          b2cPolicies.names.resetPassword
        ) {
          const activeAccount =
            this.authService.instance.getActiveAccount() ??
            this.authService.instance.getAllAccounts()[0];
          const dialogRef = this.modalService.openPopup(
            {
              isFooterRequired: true,
              isForWarning: true,
              message: popupWidth.windowAlert,
            },
            '',
            MessagePopupComponent,
            popupWidth.warningPopup
          );
          dialogRef
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(data => {
              if (data === true) {
                this.authService.logoutRedirect({
                  account: activeAccount,
                });
              }
            });
          this.spinnerService.hideSpinner();
        } else {
          if (payload) {
            this.getIPAddress(payload);
          }
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
        this.ibcAuthService.tryInitLocalStorageEncryptionKey();
        this.localStorageKeyService.migratePlainKeys();
        this.getClaims(this.authService.instance.getActiveAccount()?.idTokenClaims);
        this.userService.ensureUserProfileLoaded();
        this.restoreAuthRedirectUrl();
      });
  }

  private getJwtToken(payload: any) {
    this.ibcAuthService
      .authorizeB2CToken(payload?.accessToken, this.ipAddress)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (response: any) => {
        this.ibcAuthService.setTokenInfo(payload?.account);
        this.ibcAuthService.setJWTToken(response?.accessToken);
        this.restoreAuthRedirectUrl();
        this.userService.ensureUserProfileLoaded();
        // Releases the manual showSpinner() from b2cAuthorizeRequest(); the
        // ref-counted SpinnerService otherwise never reaches zero after login.
        this.spinnerService.hideSpinner();
      },
      error: (error: HttpErrorResponse) => {
        this.errorHandlerService.handleError(error);
        this.spinnerService.hideSpinner();
      },
    });
  }

  private spinnerStatus() {
    this.spinnerService.visibility
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        setTimeout(() => {
          this.isSpinnerLoad = data;
          this.changeDetectorRef.detectChanges();
        });
      });
  }

  private checkAndSetActiveAccount() {
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  private getClaims(claims: any) {
    let list: Claim[] = new Array<Claim>();
    if (claims) {
      Object.keys(claims).forEach(function (k, v) {
        let c = new Claim();
        c.id = v;
        c.claim = k;
        c.value = claims ? claims[k] : null;
        list.push(c);
      });
      this.dataSource = list;
    }
  }

  private login(userFlowRequest?: RedirectRequest | PopupRequest) {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this.msalGuardConfig.authRequest) {
        this.authService
          .loginPopup({
            ...this.msalGuardConfig.authRequest,
            ...userFlowRequest,
          } as PopupRequest)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((response: AuthenticationResult) => {
            this.authService.instance.setActiveAccount(response.account);
          });
      } else {
        this.authService
          .loginPopup(userFlowRequest)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((response: AuthenticationResult) => {
            this.authService.instance.setActiveAccount(response.account);
          });
      }
    } else if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
        ...userFlowRequest,
      } as RedirectRequest);
    } else {
      this.authService.loginRedirect(userFlowRequest);
    }
  }

  logout() {
    const activeAccount =
      this.authService.instance.getActiveAccount() ||
      this.authService.instance.getAllAccounts()[0];

    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.authService.logoutPopup({
        account: activeAccount,
      });
    } else {
      this.authService.logoutRedirect({
        account: activeAccount,
      });
    }
  }

  private restoreAuthRedirectUrl(): void {
    const url = this.localStorageService.getItem(STORAGE_KEYS.AUTH_REDIRECT_URL);
    if (typeof url !== 'string' || !url) {
      return;
    }
    this.localStorageService.removeItem(STORAGE_KEYS.AUTH_REDIRECT_URL);
    void this.router.navigateByUrl(url, { replaceUrl: true });
  }

  private getIPAddress(payload: any) {
    this.ibcAuthService
      .getIpAddressOfUSer()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (response: any) => {
        this.ipAddress = response.ip;
        this.getJwtToken(payload);
      },
      error: (error: HttpErrorResponse) => {
        this.errorHandlerService.handleError(error);
        this.spinnerService.hideSpinner();
      },
    });
  }

  private registerRpeRuntimeBridge(): void {
    const permissions = this.userEffectivePermissionsService;
    this.rpeRuntimeBridge.registerMenuVisibility((routerLink) =>
      permissions.isMenuItemVisible(routerLink)
    );
    this.rpeRuntimeBridge.registerClearPermissions(() =>
      permissions.clearPermissions()
    );
    this.rpeRuntimeBridge.registerFieldPermissionHandlers({
      applyToForm: (moduleNames, form, registry, pageMode, formScope) =>
        applyRpeFieldPermissionsToForm(
          permissions,
          moduleNames,
          form,
          registry,
          pageMode,
          formScope
        ),
      applyToGridForm: (moduleNames, gridForm, pageMode, formScope, registry) =>
        applyRpeFieldPermissionsToGridForm(
          permissions,
          moduleNames,
          gridForm,
          pageMode,
          formScope,
          registry
        ),
      isIdentityPermitted: (
        moduleNames,
        identityName,
        pageMode,
        formScope,
        registry
      ) =>
        isRpeRegistryFieldPermitted(
          permissions,
          moduleNames,
          identityName,
          pageMode,
          formScope,
          registry
        ),
    });
    this.rpeRuntimeBridge.registerSmCrudPopupHandlers({
      applyInfoIcons: (route, icons, isBulkEditView) =>
        this.smRpeService.applyInfoIcons(route, icons, isBulkEditView),
      subscribeLocationRefresh: (callback) =>
        this.smRpeService.subscribeLocationRefresh(this.destroyRef, callback),
    });
    this.rpeRuntimeBridge.registerCanMutateInformationTabsInViewMode(() =>
      permissions.canMutateInformationTabsInViewMode()
    );
    permissions.subscribeLocationRefresh(this.destroyRef, () =>
      this.rpeRuntimeBridge.notifyContextRefresh()
    );
  }
}
